import {
  DOODLE_BADGES,
  DOODLE_CLASSES,
  DOODLE_KEYFRAMES,
  type DoodleBadgeData,
  type DoodleBadgeId,
} from "./badges.data";

/**
 * How a doodle badge is drawn — the redesign's settings, fixed.
 *
 * The Claude Design runtime exposes knobs for all of these (shape, ring,
 * shadow, a hand-drawn wobble, motion pause and speed); the handoff froze them
 * at one value each, so they are constants here rather than props nobody may
 * change: a CIRCLE, a 2.4px ink RING, a 2px hard SHADOW at 45°, and motion on a
 * 1.5s pause at normal speed. The design's "hand" filter runs at wobble 0, which
 * is a displacement of zero — it draws nothing — so it is not ported.
 */
const INK = "#1d1d1f";
const RING_WIDTH = 2.4;
const SHADOW_DIST = 2;
const SHADOW_ANGLE_DEG = 45;
const MOTION_PAUSE_S = 1.5;
const MOTION_SPEED = 1;

const SHADOW_DISC = '<circle cx="26" cy="26.2" r="20" fill="#1d1d1f" stroke="none"/>';
const FACE_DISC = '<circle cx="24" cy="24" r="20" fill="';

const BY_ID = new Map<DoodleBadgeId, DoodleBadgeData>(
  DOODLE_BADGES.map((badge) => [badge.id, badge]),
);

export function doodleBadge(id: DoodleBadgeId): DoodleBadgeData | undefined {
  return BY_ID.get(id);
}

/** The ring around the face disc and the offset ink shadow behind it. */
function shade(svg: string): string {
  if (!svg.includes(SHADOW_DISC)) return svg;
  const angle = (SHADOW_ANGLE_DEG * Math.PI) / 180;
  const cx = (24 + SHADOW_DIST * Math.cos(angle)).toFixed(2);
  const cy = (24 + SHADOW_DIST * Math.sin(angle)).toFixed(2);
  return svg
    .replace(
      FACE_DISC,
      `<circle cx="24" cy="24" r="20" stroke="${INK}" stroke-width="${RING_WIDTH}" fill="`,
    )
    .replace(
      SHADOW_DISC,
      `<circle cx="${cx}" cy="${cy}" r="20" fill="${INK}" stroke="none"/>`,
    );
}

/**
 * The motion stylesheet, built once.
 *
 * Every animated part of a drawing carries a `dd-*` class (eyes blink, steam
 * rises, a bell rings). The design scales each keyframe so the move plays and
 * then holds for the pause, and scopes the rules under one wrapper class. It
 * is one global `<style>`, not one per badge: a sidebar of five badges would
 * otherwise carry five copies of 47 keyframes.
 *
 * Wrapped in `prefers-reduced-motion: no-preference`, so a reader who asked
 * the system for less motion gets the still drawing.
 */
const MOTION_TAG = `${Math.round(MOTION_PAUSE_S * 10)}_${Math.round(MOTION_SPEED * 10)}`;
export const DOODLE_MOTION_CLASS = `dds${MOTION_TAG}`;

function motionCss(): string {
  const pause = MOTION_PAUSE_S;
  const speed = MOTION_SPEED;
  let css = "";
  for (const [name, [duration, frames]] of Object.entries(DOODLE_KEYFRAMES)) {
    const ratio = (duration * speed) / (duration * speed + pause);
    let last = "";
    const body = frames.replace(
      /([\d.%,]+)\{([^}]*)\}/g,
      (_match, selector: string, declarations: string) => {
        const points = selector.split(",").map((point) => parseFloat(point));
        if (points.includes(100)) last = declarations;
        return `${points.map((point) => `${+(point * ratio).toFixed(2)}%`).join(",")}{${declarations}}`;
      },
    );
    css += `@keyframes dd${name}${MOTION_TAG}{${body}${pause && last ? `100%{${last}}` : ""}}`;
  }
  for (const [className, keyframe, easing, delay, extra] of DOODLE_CLASSES) {
    const duration = DOODLE_KEYFRAMES[keyframe]?.[0];
    if (duration === undefined) continue;
    css += `.${DOODLE_MOTION_CLASS} .${className}{animation:dd${keyframe}${MOTION_TAG} ${(duration * speed + pause).toFixed(2)}s ${easing} ${(delay * speed).toFixed(2)}s infinite;${extra}}`;
  }
  return `@media (prefers-reduced-motion: no-preference){${css}}`;
}

const STYLE_ID = "doodle-badge-motion";

/** Puts the motion stylesheet in `<head>` the first time an animated badge renders. */
export function ensureDoodleMotionStyles(): void {
  if (typeof document === "undefined") return;
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = motionCss();
  document.head.appendChild(style);
}

/** The inner markup of a badge's 48×48 SVG, shaded, optionally animated. */
export function doodleBadgeMarkup(id: DoodleBadgeId, animate: boolean): string | null {
  const badge = BY_ID.get(id);
  if (!badge) return null;
  const shaded = shade(badge.svg);
  return animate ? `<g class="${DOODLE_MOTION_CLASS}">${shaded}</g>` : shaded;
}
